/**
 * P0.5.15 — Athlete-profile Match Exposure card.
 *
 * Thin wrapper that reuses the canonical `AthleteMatchExposureSection`
 * component (P0.5.13A) with a compact period selector matching the
 * Training-Distribution card UX (Last 30d / 90d / 365d / Season).
 *
 * IMPORTANT — this component MUST NOT recompute any Match Exposure
 * value. All KPIs and series come from:
 *   GET /reports/match-exposure/athlete/{athlete_id}?date_from&date_to
 *   GET /reports/match-exposure/athlete/{athlete_id}/timeseries
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Clock } from 'lucide-react';
import AthleteMatchExposureSection from '../reports/AthleteMatchExposureSection';

const RANGES = [
  { key: '30',  label: 'Last 30d',  label_it: 'Ultimi 30g',  days: 30 },
  { key: '90',  label: 'Last 90d',  label_it: 'Ultimi 90g',  days: 90 },
  { key: '365', label: 'Last 365d', label_it: 'Ultimi 365g', days: 365 },
  { key: 'season', label: 'Season', label_it: 'Stagione',   days: null },
];

const isoDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const today = () => new Date().toISOString().slice(0, 10);

// Current football season (Aug → Jul).
const seasonWindow = () => {
  const d = new Date();
  const start = d.getMonth() >= 7 ? d.getFullYear() : d.getFullYear() - 1;
  return { date_from: `${start}-08-01`, date_to: `${start + 1}-07-31` };
};

export default function MatchExposureCard({ athleteId }) {
  const { api } = useAuth();
  const { language } = useLanguage();
  const t = (en, it) => (language === 'IT' ? it : en);

  const [range, setRange] = useState('90');
  const [teamId, setTeamId] = useState(null);

  // Resolve athlete's primary team once — Match Exposure denominator
  // is team-scoped, so we always pass team_id.
  useEffect(() => {
    if (!athleteId) return;
    let alive = true;
    api
      .get(`/athletes/${athleteId}`)
      .then((r) => {
        if (!alive) return;
        const primary = (r.data?.teams || []).find(
          (x) => x.assignment_type === 'PRIMARY',
        ) || (r.data?.teams || [])[0];
        setTeamId(primary?.team_id || null);
      })
      .catch(() => {
        if (alive) setTeamId(null);
      });
    return () => {
      alive = false;
    };
  }, [athleteId, api]);

  const { dateFrom, dateTo } = useMemo(() => {
    const sel = RANGES.find((r) => r.key === range) || RANGES[1];
    if (sel.key === 'season') {
      const w = seasonWindow();
      return { dateFrom: w.date_from, dateTo: w.date_to };
    }
    return { dateFrom: isoDaysAgo(sel.days), dateTo: today() };
  }, [range]);

  return (
    <Card
      className="bg-zinc-900/50 border-zinc-800"
      data-testid="athlete-match-exposure-card"
    >
      <CardHeader className="flex flex-row items-center justify-between pb-3 gap-2">
        <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
          <Clock className="w-4 h-4 text-cyan-400" />
          {t('Match Exposure', 'Match Exposure')}
        </CardTitle>
        <div className="flex items-center gap-1">
          {RANGES.map((r) => (
            <Button
              key={r.key}
              size="sm"
              variant={range === r.key ? 'default' : 'ghost'}
              onClick={() => setRange(r.key)}
              className={`h-7 px-2 text-xs ${
                range === r.key
                  ? 'bg-cyan-500 text-zinc-950 hover:bg-cyan-400'
                  : 'text-zinc-400'
              }`}
              data-testid={`me-card-range-${r.key}`}
            >
              {t(r.label, r.label_it)}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {athleteId && (
          <AthleteMatchExposureSection
            api={api}
            athleteId={athleteId}
            teamId={teamId}
            dateFrom={dateFrom}
            dateTo={dateTo}
            t={t}
          />
        )}
      </CardContent>
    </Card>
  );
}
